import { faArrowUpRightFromSquare, faFileLines } from '@fortawesome/pro-solid-svg-icons';
import { Box, Typography, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';

import pdf from '../../assets/pdf/forklaring---personkreditt-forbruker-04.09.24.pdf';
import { Coin } from '../../assets/svg/Coin';
import Description from '../../components/Description/Description';
import Icon from '../../components/Icon/Icon';
import Information from '../../components/Information/Information';
import InformationRow from '../../components/Information/InformationRow';
import Link from '../../components/Link/Link';
import ListField from '../../components/ListField/ListField';
import ShowDetailsModal from '../../components/ShowDetailsModal/ShowDetailsModal';
import Tabs from '../../components/Tabs/Tabs';
import {
    ContentHeader,
    Contet,
    StyledCard,
    StyledCardContent,
    ToggleGroup,
} from '../../components/ToggleOfferField/ToggleOfferField.styles';
import { formatNumberByThousands } from '../../helpers/formatNumberByThousands';
import { formatYearsAndMonths } from '../../helpers/formatYearsAndMonths';
import { getOfferTitleTranslationKey } from '../../helpers/getOfferTitleTranslationKey';
import useGetFinalOffer from '../../hooks/useGetFinalOffer';
import useSettings from '../../hooks/useSettings';
import useTranslation from '../../hooks/useTranslation';
import ConsolidationsList from '../SelectOfferPage/ConsolidationsList';

const CoApplicantSignAgreementPageSidePanel = () => {
    const translate = useTranslation();
    const theme = useTheme();
    const { countryCode, languageCode } = useSettings();
    const { finalOffer } = useGetFinalOffer();
    const [isOfferUpdated, setIsOfferUpdated] = useState(false);
    const [activeTab, setActiveTab] = useState<'details' | 'loans'>('details');

    useEffect(() => {
        if (
            finalOffer?.selected.approved.amount !== finalOffer?.current.approved.amount ||
            finalOffer?.selected.requiredConsolidation.amount !== finalOffer?.current.requiredConsolidation.amount ||
            finalOffer?.selected.monthlyInstallment.amount !== finalOffer?.current.monthlyInstallment.amount ||
            finalOffer?.selected.tenor.months !== finalOffer?.current.tenor.months ||
            finalOffer?.selected.nominalInterest.percent !== finalOffer?.current.nominalInterest.percent ||
            finalOffer?.selected.effectiveInterest.percent !== finalOffer?.current.effectiveInterest.percent
        ) {
            setIsOfferUpdated(true);
        }
    }, [finalOffer, isOfferUpdated, setIsOfferUpdated]);

    const hasCoApplicantLoan = finalOffer?.current?.consolidations?.some(
        (consolidation) => consolidation.applicantType === 'CO',
    );

    const noApplicantType = finalOffer?.current?.consolidations?.some((consolidation) => !consolidation.applicantType);

    return (
        <>
            <ToggleGroup isOneOffer={true}>
                <StyledCard isOneOffer={true}>
                    <StyledCardContent>
                        <ContentHeader>
                            <Coin />
                            <Typography variant="subtitle2" color={'grey.900'}>
                                {translate(
                                    getOfferTitleTranslationKey(
                                        finalOffer?.current.offerType,
                                        !!finalOffer?.current.offerType && finalOffer.current.offerType !== 'CASH',
                                        countryCode,
                                        languageCode,
                                    ),
                                )}
                            </Typography>
                        </ContentHeader>
                        <Contet>
                            <Typography variant="body1" color={theme.palette.text.secondary}>
                                {translate('field.amount.label')}:
                            </Typography>
                            {!isOfferUpdated ? (
                                <>
                                    <Typography variant="h3" color={'grey.900'}>
                                        {`${formatNumberByThousands(finalOffer?.selected.approved.amount)} ${translate(
                                            'field.currency.label',
                                        )}`}
                                    </Typography>
                                    <Typography variant="body2">
                                        {`${translate('field.toggle.tenor.label')} ${formatYearsAndMonths(
                                            Number(finalOffer?.selected.tenor.months),
                                            translate,
                                        )}`}
                                    </Typography>
                                </>
                            ) : (
                                <>
                                    <Typography variant="h3" color={'grey.900'}>
                                        {`${formatNumberByThousands(finalOffer?.current.approved.amount)} ${translate(
                                            'field.currency.label',
                                        )}`}
                                    </Typography>
                                    <Typography variant="body2">
                                        {`${translate('field.toggle.tenor.label')} ${formatYearsAndMonths(
                                            Number(finalOffer?.current.tenor.months),
                                            translate,
                                        )} `}
                                    </Typography>
                                </>
                            )}
                        </Contet>
                    </StyledCardContent>
                </StyledCard>
            </ToggleGroup>

            {isOfferUpdated && (
                <Box paddingTop={'16px'}>
                    <Description text={translate('page.sign-agreement.details-changed.label')} />
                    <ShowDetailsModal
                        label={translate('dialog.sign-agreement.label')}
                        dialogTitle={translate('dialog.sign-agreement.label')}
                        dialogContent={translate('dialog.sign-agreement.content')}
                    />
                </Box>
            )}

            {finalOffer?.current?.consolidations && finalOffer?.current?.consolidations.length > 0 && (
                <Tabs
                    tabs={[
                        {
                            label: translate('page.select-offer.tab.details.title'),
                            value: 'details',
                        },
                        { label: translate('page.select-offer.tab.loans.title'), value: 'loans' },
                    ]}
                    value={activeTab}
                    onChange={(val) => setActiveTab(val as 'details' | 'loans')}
                />
            )}

            {activeTab === 'loans' && finalOffer?.current?.consolidations && (
                <Box pt={'20px'}>
                    <Typography variant="subtitle1" pb={'12px'}>
                        {translate('page.select-offer.consolidations.title')}
                    </Typography>
                    {noApplicantType ? (
                        <ConsolidationsList consolidations={finalOffer?.current.consolidations ?? []} />
                    ) : hasCoApplicantLoan ? (
                        <>
                            <Typography variant="subtitle1" fontSize={'14px'}>
                                {translate('field.consolidations.applicant.subtitle')}
                            </Typography>
                            <ConsolidationsList
                                consolidations={
                                    finalOffer?.current.consolidations?.filter((c) => c.applicantType === 'MAIN') ?? []
                                }
                            />
                            <Typography variant="subtitle1" fontSize={'14px'} pt={'12px'}>
                                {translate('field.consolidations.co-applicant.subtitle')}
                            </Typography>
                            <ConsolidationsList
                                consolidations={
                                    finalOffer?.current.consolidations?.filter((c) => c.applicantType === 'CO') ?? []
                                }
                            />
                        </>
                    ) : (
                        <ConsolidationsList consolidations={finalOffer?.current.consolidations ?? []} />
                    )}
                </Box>
            )}

            {activeTab === 'details' && (
                <Box>
                    <Information>
                        {finalOffer?.selected.approved.amount !== finalOffer?.current.approved.amount ? (
                            <>
                                <InformationRow
                                    icon={true}
                                    name={translate('field.amount.label.updated')}
                                    value={`${formatNumberByThousands(finalOffer?.current.approved.amount)} ${translate(
                                        'field.currency.label',
                                    )}`}
                                />
                                <InformationRow
                                    icon={true}
                                    name={translate('field.application.amount.label')}
                                    value={`${formatNumberByThousands(finalOffer?.selected.approved.amount)} ${translate(
                                        'field.currency.label',
                                    )}`}
                                />
                            </>
                        ) : (
                            <InformationRow
                                name={translate('field.amount.label')}
                                value={`${formatNumberByThousands(finalOffer?.selected.approved.amount)} ${translate(
                                    'field.currency.label',
                                )}`}
                            />
                        )}
                        {finalOffer?.selected.preliminaryDisbursement.amount !==
                        finalOffer?.current.preliminaryDisbursement.amount ? (
                            <>
                                <InformationRow
                                    icon={true}
                                    name={translate('field.preliminary-disbursement.label.updated')}
                                    value={`${formatNumberByThousands(
                                        finalOffer?.current.preliminaryDisbursement.amount,
                                    )} ${translate('field.currency.label')}`}
                                />
                                <InformationRow
                                    icon={true}
                                    name={translate('field.preliminary-disbursement.label')}
                                    value={`${formatNumberByThousands(
                                        finalOffer?.selected.preliminaryDisbursement.amount,
                                    )} ${translate('field.currency.label')}`}
                                />
                            </>
                        ) : finalOffer && finalOffer?.selected.preliminaryDisbursement.amount > 0 ? (
                            <InformationRow
                                name={translate('field.preliminary-disbursement.label')}
                                value={`${formatNumberByThousands(
                                    finalOffer?.selected.preliminaryDisbursement.amount,
                                )} ${translate('field.currency.label')}`}
                            />
                        ) : null}
                        {finalOffer?.selected.requiredConsolidation.amount !==
                        finalOffer?.current.requiredConsolidation.amount ? (
                            <>
                                <InformationRow
                                    icon={true}
                                    name={translate('field.required-consolidation.label.updated')}
                                    value={`${formatNumberByThousands(
                                        finalOffer?.current.requiredConsolidation.amount,
                                    )} ${translate('field.currency.label')}`}
                                />
                                <InformationRow
                                    name={translate('field.required-consolidation.label')}
                                    value={`${formatNumberByThousands(
                                        finalOffer?.selected.requiredConsolidation.amount,
                                    )} ${translate('field.currency.label')}`}
                                />
                            </>
                        ) : (
                            <InformationRow
                                name={translate('field.required-consolidation.label')}
                                value={`${formatNumberByThousands(
                                    finalOffer?.selected?.requiredConsolidation.amount,
                                )} ${translate('field.currency.label')}`}
                            />
                        )}
                        {finalOffer?.selected.monthlyInstallment.amount !==
                        finalOffer?.current.monthlyInstallment.amount ? (
                            <>
                                <InformationRow
                                    icon={true}
                                    name={translate('field.monthly-installment.label.updated')}
                                    value={`${formatNumberByThousands(
                                        finalOffer?.current.monthlyInstallment.amount,
                                    )} ${translate('field.currency.label')}`}
                                />
                                <InformationRow
                                    name={translate('field.monthly-installment.label')}
                                    value={`${formatNumberByThousands(
                                        finalOffer?.selected.monthlyInstallment.amount,
                                    )} ${translate('field.currency.label')}`}
                                />
                            </>
                        ) : (
                            <InformationRow
                                name={translate('field.monthly-installment.label')}
                                value={`${formatNumberByThousands(
                                    finalOffer?.selected.monthlyInstallment.amount,
                                )} ${translate('field.currency.label')}`}
                            />
                        )}
                        {finalOffer?.selected.tenor.months !== finalOffer?.current.tenor.months ? (
                            <>
                                <InformationRow
                                    icon={true}
                                    name={translate('field.tenor.label.updated')}
                                    value={`${formatYearsAndMonths(Number(finalOffer?.current.tenor.months), translate)} `}
                                />
                                <InformationRow
                                    name={translate('field.tenor.label')}
                                    value={`${formatYearsAndMonths(Number(finalOffer?.selected.tenor.months), translate)} `}
                                />
                            </>
                        ) : (
                            <InformationRow
                                name={translate('field.tenor.label')}
                                value={`${formatYearsAndMonths(Number(finalOffer?.selected.tenor.months), translate)} `}
                            />
                        )}
                        {finalOffer?.selected.nominalInterest.percent !==
                        finalOffer?.current.nominalInterest.percent ? (
                            <>
                                <InformationRow
                                    icon={true}
                                    name={translate('field.nominal-interest.label.updated')}
                                    value={`${formatNumberByThousands(finalOffer?.current.nominalInterest.percent)} ${'%'}`}
                                />
                                <InformationRow
                                    name={translate('field.nominal-interest.label')}
                                    value={`${formatNumberByThousands(
                                        finalOffer?.selected.nominalInterest.percent,
                                    )} ${'%'}`}
                                />
                            </>
                        ) : (
                            <InformationRow
                                name={translate('field.nominal-interest.label')}
                                value={`${formatNumberByThousands(finalOffer?.selected.nominalInterest.percent)}  ${'%'}`}
                            />
                        )}
                        {finalOffer?.selected.effectiveInterest.percent !==
                        finalOffer?.current.effectiveInterest.percent ? (
                            <>
                                <InformationRow
                                    icon={true}
                                    name={translate('field.effective-interest.label.updated')}
                                    value={`${formatNumberByThousands(
                                        finalOffer?.current.effectiveInterest.percent,
                                    )} ${'%'}`}
                                />
                                <InformationRow
                                    name={translate('field.effective-interest.label')}
                                    value={`${formatNumberByThousands(
                                        finalOffer?.selected.effectiveInterest.percent,
                                    )} ${'%'}`}
                                />
                            </>
                        ) : (
                            <InformationRow
                                name={translate('field.effective-interest.label')}
                                value={`${formatNumberByThousands(finalOffer?.selected.effectiveInterest.percent)} ${'%'}`}
                            />
                        )}
                    </Information>
                </Box>
            )}

            <Typography variant="body2">{translate('field.sign-agreement.label')}</Typography>
            <ListField
                name="signAgreementPdf"
                hideDivider={true}
                styling={true}
                items={[
                    {
                        index: '1',
                        icon: <Icon icon={faFileLines} />,
                        label: (
                            <Link onClick={() => window.open(pdf, '_blank')}>
                                <Typography variant="body2">{translate('field.sign-agreement.pdf.label')}</Typography>
                            </Link>
                        ),
                        endIcon: <Icon icon={faArrowUpRightFromSquare} size="sm" />,
                    },
                ]}
            />
        </>
    );
};

export default CoApplicantSignAgreementPageSidePanel;
